import { MouseEventHandler, ReactNode } from "react";
import {
  Button,
  List,
  ListItem,
  ListItemProps,
  ListProps,
  Tag,
  TagProps,
  useDisclosure,
} from "@chakra-ui/react";
import { formatInteger } from "utils/formatting";
import Link from "components/Shared/Link";
import { Url } from "utils/links";

type TagListItemProps = Omit<TagProps & ListItemProps, "children">;

export type TagListProps<D> = TagListItemProps & {
  getItemProps?: (item: D) => TagListItemProps;
  getKey: (item: D) => string | number;
  getLabel: (item: D) => ReactNode;
  items: D[] | null | undefined;
  listProps?: ListProps;
  moreHref?: Url;
  numTotal?: number;
  onMoreClick?: MouseEventHandler<HTMLAnchorElement & HTMLButtonElement>;
};

export default function TagList<D>({
  getItemProps,
  getKey,
  getLabel,
  items,
  listProps,
  moreHref,
  numTotal,
  onMoreClick,
  ...rest
}: TagListProps<D>) {
  const numMore =
    typeof numTotal === "number" && items ? numTotal - items.length : undefined;

  const moreText = numMore ? `+${formatInteger(numMore)} more` : null;

  const moreContent = (() => {
    if (moreHref) {
      return (
        <Link href={moreHref} onClick={onMoreClick}>
          {moreText}
        </Link>
      );
    }

    if (onMoreClick) {
      return (
        <Button
          onClick={onMoreClick}
          variant="unstyled"
          fontSize="inherit"
          textTransform="none"
        >
          {moreText}
        </Button>
      );
    }

    return moreText;
  })();

  return (
    <List
      display="inline-flex"
      alignItems="baseline"
      flexWrap="wrap"
      maxW="100%"
      gap={1}
      {...listProps}
    >
      {items?.map((item) => (
        <Tag
          key={getKey(item)}
          as={ListItem}
          className="chakra-tag"
          display="inline-block"
          minH="1em"
          py=".2em"
          fontSize=".875em"
          {...getItemProps?.(item)}
          {...rest}
        >
          {getLabel(item)}
        </Tag>
      ))}

      {numMore && (
        <ListItem
          display="inline-block"
          fontSize=".7em"
          color="gray.500"
          ml=".25em"
          isTruncated
          lineHeight="normal"
          sx={{ "tr[aria-selected=true] &": { color: "gray.400" } }}
        >
          {moreContent}
        </ListItem>
      )}
    </List>
  );
}

type ExpandableTagListProps<D> = Omit<TagListProps<D>, "numTotal"> & {
  limit?: number;
};

export function ExpandableTagList<D>({
  items,
  limit = 3,
  ...rest
}: ExpandableTagListProps<D>) {
  const { isOpen, onToggle } = useDisclosure();
  return (
    <TagList
      items={isOpen ? items : items?.slice(0, limit)}
      numTotal={items?.length}
      onMoreClick={(evt) => {
        evt.stopPropagation();
        onToggle();
      }}
      {...rest}
    />
  );
}
